import { type ReactNode } from "react";
import { cn } from "../lib/utils.js";
import { EYEBROW } from "../lib/typography.js";

/* The page header of a settings or admin page (PB-24). One of the three
   settings-page parts, with SettingsGroup and SettingRow. Ported from the
   wireframe settings shell. */

type SectionHeaderVariant = "default" | "settings" | "eyebrow";
type SectionHeaderSize = "sm" | "md" | "lg";

interface SectionHeaderBaseProps {
  title: string;
  /** The line under the title. Omit it and no empty paragraph renders. */
  description?: string;
  /** Small badge beside the title (the canvas' "Admin" tag on KPI Manager). */
  badge?: ReactNode;
  /** Inline muted text after the title and the badge (a count, a status). */
  meta?: ReactNode;
  /**
   * Keep the title for assistive technology and take it off the screen, for a
   * section whose name is already on screen right above it — the selected tab.
   * The description still shows, which is the part that carries information.
   */
  titleHidden?: boolean;
  /**
   * Heading level, default 1: the page's own title. Use 2 for a header that
   * introduces a SECTION of a page that already has a title — a tab panel
   * most often — and 3 below that, so the outline a screen reader navigates
   * by stays true.
   */
  headingLevel?: 1 | 2 | 3;
  /** A visual before the title: an icon, a mark, an avatar. */
  leading?: ReactNode;
  /** The trailing action group (a primary action button, a menu). */
  actionsSlot?: ReactNode;
}

type SectionHeaderVariantProps =
  | {
      /**
       * `settings` renders the Direction B page header (PB-5134): heading-1
       * title and a hairline under the header. `eyebrow` renders the title in
       * the shared EYEBROW style, meta inline, no hairline. Both are opt-in so
       * the admin pages that share this header keep their current look.
       */
      variant?: "default" | "settings";
      /**
       * Title size: `sm` heading-3, `md` text-xl, `lg` heading-1, at any
       * heading level. Omitted, it follows the level and the variant: level 2
       * or 3 is `sm`, else `settings` is `lg` and `default` is `md`.
       */
      size?: SectionHeaderSize;
    }
  | {
      variant: "eyebrow";
      /** An eyebrow has one size, the shared EYEBROW scale. */
      size?: never;
    };

export type SectionHeaderProps = SectionHeaderBaseProps &
  SectionHeaderVariantProps;

const TITLE_SIZE: Record<SectionHeaderSize, string> = {
  sm: "text-heading-3 tracking-tight text-pb-text",
  md: "text-xl font-semibold text-pb-text tracking-tight",
  lg: "text-heading-1 tracking-tight text-pb-text",
};

const DEFAULT_ROW = "flex items-center gap-2 mb-1";
const DEFAULT_META = "text-sm text-pb-text-muted";
const DEFAULT_DESCRIPTION =
  "text-pb-text-secondary text-sm max-w-xl leading-relaxed";

const LAYOUT: Record<
  SectionHeaderVariant,
  { root: string; row: string; meta: string; description: string }
> = {
  default: {
    root: "flex items-start justify-between gap-4 flex-wrap mb-6",
    row: DEFAULT_ROW,
    meta: DEFAULT_META,
    description: DEFAULT_DESCRIPTION,
  },
  settings: {
    root: "mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-pb-border/70 pb-4.5",
    row: DEFAULT_ROW,
    meta: DEFAULT_META,
    description: DEFAULT_DESCRIPTION,
  },
  eyebrow: {
    root: "mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2",
    row: "flex items-center gap-2",
    meta: "text-dense-2 text-pb-text-muted",
    description: `mt-1 ${DEFAULT_DESCRIPTION}`,
  },
};

/** Page-level header for a settings section. */
export function SectionHeader({
  title,
  description,
  leading,
  actionsSlot,
  variant = "default",
  size,
  badge,
  meta,
  headingLevel: level = 1,
  titleHidden = false,
}: SectionHeaderProps) {
  const Heading = `h${level}` as const;
  const layout = LAYOUT[variant];
  const titleClass =
    variant === "eyebrow"
      ? EYEBROW
      : TITLE_SIZE[
          size ?? (level > 1 ? "sm" : variant === "settings" ? "lg" : "md")
        ];
  return (
    <div className={layout.root}>
      <div className="min-w-0">
        <div className={layout.row}>
          {leading}
          <Heading
            className={cn(titleHidden && "sr-only", !titleHidden && titleClass)}
          >
            {title}
          </Heading>
          {badge}
          {meta != null && meta !== false ? (
            <span className={layout.meta}>{meta}</span>
          ) : null}
        </div>
        {description ? (
          <p className={layout.description}>{description}</p>
        ) : null}
      </div>
      {actionsSlot}
    </div>
  );
}
